import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, CheckCircle, MessageSquare, AlertCircle } from 'lucide-react';
import { enqueueSnackbar } from 'notistack';
import CreateQuestaoModal from './CreateQuestaoModal';
import { provasService } from '../../services/provas';
import { ProvaQuestao } from '../../types/provas';

interface QuestoesManagerProps {
  isOpen: boolean;
  onClose: () => void;
  provaId: string;
  provaTitle: string;
}

const QuestoesManager: React.FC<QuestoesManagerProps> = ({
  isOpen,
  onClose,
  provaId,
  provaTitle
}) => {
  const [questoes, setQuestoes] = useState<ProvaQuestao[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [questaoEditando, setQuestaoEditando] = useState<ProvaQuestao | undefined>();

  useEffect(() => {
    if (isOpen) {
      carregarQuestoes();
    }
  }, [isOpen, provaId]);

  const carregarQuestoes = async () => {
    try {
      setLoading(true);
      const response = await provasService.listarQuestoes(provaId);
      setQuestoes(response);
    } catch (error) {
      console.error('Erro ao carregar questões:', error);
      enqueueSnackbar('Erro ao carregar questões', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const abrirModalCriacao = () => {
    setQuestaoEditando(undefined);
    setIsModalOpen(true);
  };

  const abrirModalEdicao = (questao: ProvaQuestao) => {
    setQuestaoEditando(questao);
    setIsModalOpen(true);
  };

  const handleCriarQuestao = async (questaoData: any) => {
    try {
      const createData = {
        id_prova: provaId,
        ...questaoData
      };
      await provasService.criarQuestao(createData);
      setIsModalOpen(false);
      await carregarQuestoes();
      enqueueSnackbar('Questão criada com sucesso!', { variant: 'success' });
    } catch (error) {
      console.error('Erro ao criar questão:', error);
      enqueueSnackbar('Erro ao criar questão', { variant: 'error' });
    }
  };

  const handleEditarQuestao = async (questaoData: any) => {
    if (!questaoEditando) return;

    try {
      await provasService.atualizarQuestao(questaoEditando.id, questaoData);
      setIsModalOpen(false);
      setQuestaoEditando(undefined);
      await carregarQuestoes();
      enqueueSnackbar('Questão atualizada com sucesso!', { variant: 'success' });
    } catch (error) {
      console.error('Erro ao atualizar questão:', error);
      enqueueSnackbar('Erro ao atualizar questão', { variant: 'error' });
    }
  };  const handleExcluirQuestao = async (questao: ProvaQuestao) => {
    if (!confirm(`Tem certeza que deseja excluir a questão ${questao.ordem}?`)) {
      return;
    }

    try {
      await provasService.excluirQuestao(questao.id);
      await carregarQuestoes();
      enqueueSnackbar('Questão excluída com sucesso!', { variant: 'success' });
    } catch (error) {
      console.error('Erro ao excluir questão:', error);
      enqueueSnackbar('Erro ao excluir questão', { variant: 'error' });
    }
  };

  const getProximaOrdem = () => {
    if (questoes.length === 0) return 1;
    return Math.max(...questoes.map(q => q.ordem)) + 1;
  };

  const getTipoIcon = (tipo: string) => {
    return tipo === 'multipla_escolha' ? <CheckCircle size={16} className="text-blue-500" /> : <MessageSquare size={16} className="text-green-500" />;
  };

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-card rounded-lg p-8">
          <div className="text-center">Carregando questões...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-card rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h2 className="text-xl font-semibold">Gerenciar Questões</h2>
              <p className="text-muted-foreground text-sm mt-1">{provaTitle}</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={abrirModalCriacao}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              >
                <Plus size={16} />
                Nova Questão
              </button>
              <button
                onClick={onClose}
                className="text-muted-foreground hover:text-foreground"
              >
                ✕
              </button>
            </div>
          </div>

          <div className="p-6">
            {questoes.length === 0 ? (
              <div className="text-center py-12">
                <AlertCircle size={48} className="text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">
                  Nenhuma questão cadastrada
                </h3>
                <p className="text-muted-foreground mb-6">
                  Adicione questões para que os membros possam realizar a prova.
                </p>
                <button
                  onClick={abrirModalCriacao}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  Criar primeira questão
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {questoes.map((questao) => (
                  <div
                    key={questao.id}
                    className="border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                            <span className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300 px-2 py-1 rounded text-sm font-medium">
                            #{questao.ordem}
                          </span>
                          {getTipoIcon(questao.tipo)}
                            <span className="text-sm text-muted-foreground capitalize">
                            {questao.tipo.replace('_', ' ')}
                          </span>
                            <span className="bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 px-2 py-1 rounded text-sm">
                            {questao.pontuacao} pts
                          </span>
                          {!questao.ativa && (
                            <span className="bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300 px-2 py-1 rounded text-sm">
                              Inativa
                            </span>
                          )}
                        </div>

                        <h4 className="font-medium text-foreground mb-2 whitespace-pre-wrap">
                          {questao.enunciado}
                        </h4>

                        {questao.tipo === 'multipla_escolha' && (questao.opcoes || questao.__opcoes__) && (
                          <div className="space-y-1 mb-2">
                            {(questao.opcoes || questao.__opcoes__).map((opcao) => (
                              <div
                                key={opcao.id}
                                className={`flex items-center gap-2 text-sm ${
                                  opcao.correta ? 'text-green-700 dark:text-green-400 font-medium' : 'text-muted-foreground'
                                }`}
                              >
                                <span className="font-medium">{opcao.letra})</span>
                                <span>{opcao.texto}</span>
                                {opcao.correta && <CheckCircle size={14} className="text-green-500" />}
                              </div>
                            ))}
                          </div>
                        )}

                        {questao.tipo === 'texto_livre' && questao.resposta_texto_correta && (
                          <div className="text-sm text-muted-foreground mb-2">
                            <span className="font-medium">Resposta esperada:</span> {questao.resposta_texto_correta}
                          </div>
                        )}

                        {questao.explicacao && (
                          <div className="text-sm text-muted-foreground">
                            <span className="font-medium">Explicação:</span> {questao.explicacao}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => abrirModalEdicao(questao)}
                          className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                          title="Editar questão"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleExcluirQuestao(questao)}
                          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                          title="Excluir questão"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <CreateQuestaoModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setQuestaoEditando(undefined);
        }}
        onSubmit={questaoEditando ? handleEditarQuestao : handleCriarQuestao}
        questaoEditando={questaoEditando}
        proximaOrdem={getProximaOrdem()}
      />
    </>
  );
};

export default QuestoesManager;
